import React from 'react'
import cx from 'classnames'

import { makeTranslate, getString } from '../../../utils/localization/format'
import List from '../../List'
import Scrollbars from '../../Scrollbars'

import { BasicOption, OptionsListProps } from './interfaces'
import { Option } from './Option'
import { useOptionsList } from './useOptionsList'

// eslint-disable-next-line @typescript-eslint/naming-convention
const defaultEmptyLabel = makeTranslate('No Items Found')

export const OptionsList = <OptionType extends BasicOption>({
  options,
  renderOption,
  onKeyUp,
  emptyLabel,
  selectedOption,
  disableAutoScroll = false,
  handleSelect,
  tabIndex,
  focusedId,
  setFocusedId,
  onKeydown,
  loading,
  listRef: externalListRef,
}: OptionsListProps<OptionType>) => {
  const { listRef, scrollerRef } = useOptionsList({
    options,
    focusedId,
    disableAutoScroll,
    externalListRef,
  })

  return (
    <Scrollbars autoHeight autoHeightMax={280} autoHide={false} ref={scrollerRef}>
      {
        <List
          role="menu"
          ref={listRef}
          onKeyDown={onKeydown}
          loading={loading}
          onKeyUp={onKeyUp}
          // if search is rendered, search input keeps focus and list doesn't need to be focusable.
          // without search list needs tabIndex=0 to prevent focus from jumping to the next element
          tabIndex={tabIndex}
          aria-activedescendant={tabIndex >= 0 ? focusedId : undefined}
        >
          {options.length > 0 ? (
            options.map((option, index) => (
              <Option
                key={`${option.label}_${index}`}
                option={option}
                focused={focusedId === option.id}
                selected={selectedOption?.value === option.value}
                handleSelect={handleSelect}
                setFocusedId={setFocusedId}
                renderOption={renderOption}
              />
            ))
          ) : (
            <div className={cx('p-y-xs p-x text-secondary')}>
              {emptyLabel ?? getString(defaultEmptyLabel)}
            </div>
          )}
        </List>
      }
    </Scrollbars>
  )
}
